import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getStatusColor } from '../lib/statusColors';
import { 
  FileUp, 
  Upload,
  Trash2,
  Search,
  Calendar,
  Package,
  Building,
  Eye,
  FileText,
  Download,
  User,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from "lucide-react";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface SubmittedReturn {
  id?: string;
  submissionId: string;
  packageId: string;
  packageName: string;
  packageNumber: string;
  packageDescription?: string;
  description?: string;
  submittedAt: string;
  notes?: string;
  status: string;
  recipientCompany: string;
  returnedBy?: string;
  returnedByEmail?: string;
  companyName?: string;
  hubName?: string;
  projectName?: string;
  filesCount?: number;
  filesUploaded?: number;
  files?: ReturnFile[];
  returnReason?: string;
  isApproved?: boolean;
  approvedBy?: string;
  approvedAt?: string;
}

interface ReturnFile {
  id: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

interface TenderPackage {
  id: string;
  name: string;
  packageNumber: string;
  description?: string;
  totalFiles: number;
  createdAt: string;
  submissionDeadline?: string;
  sentAt: string;
  status: string;
  hubName?: string;
  projectName?: string;
  shareToken?: string;
}

interface TendererReturnsProps {
  authData: TendererAuth;
}

export function TendererReturns({ authData }: TendererReturnsProps) {
  const { toast } = useToast();
  const params = useParams<{ projectId?: string; companyCode?: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TenderPackage | null>(null);
  const [submissionFiles, setSubmissionFiles] = useState<File[]>([]);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [selectedReturn, setSelectedReturn] = useState<SubmittedReturn | null>(null);
  const [showReturnDetailDialog, setShowReturnDetailDialog] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Fetch submitted returns
  const { data: submittedReturns = [], isLoading: returnsLoading, refetch: refetchReturns } = useQuery<SubmittedReturn[]>({
    queryKey: ['/api/tenderer/submitted-returns', authData?.email, authData?.accessCode, params?.projectId],
    queryFn: () => apiRequest(`/api/tenderer/submitted-returns?email=${encodeURIComponent(authData?.email || '')}&accessCode=${encodeURIComponent(authData?.accessCode || '')}&projectId=${params?.projectId || ''}`, {
      method: 'GET'
    }),
    enabled: !!authData?.email && !!authData?.accessCode && !!params?.projectId,
  });

  // Fetch available packages for submission
  const { data: packages = [] } = useQuery<TenderPackage[]>({
    queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode, params?.projectId],
    queryFn: () => apiRequest('/api/tenderer/packages', {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode,
        projectId: params?.projectId
      })
    }),
    enabled: !!authData?.email && !!authData?.accessCode && !!params?.projectId,
  });

  const handleTenderReturn = async () => {
    if (!selectedPackage || submissionFiles.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to submit.",
        variant: "destructive",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('packageId', selectedPackage.id);
      formData.append('recipientName', authData?.email?.split('@')[0] || '');
      formData.append('recipientEmail', authData?.email || '');
      formData.append('recipientCompany', authData?.companyName || '');
      formData.append('companyAccessCode', authData?.accessCode || '');
      formData.append('notes', submissionNotes);
      
      submissionFiles.forEach((file) => {
        formData.append(`files`, file);
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001'}/api/tenderer/submit-return`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast({
          title: "Submission successful",
          description: "Your tender return has been submitted successfully.",
        });
        setShowSubmissionDialog(false);
        setSubmissionFiles([]);
        setSubmissionNotes('');
        setSelectedPackage(null);
        
        // Refresh returns
        refetchReturns();
      } else {
        throw new Error(`Submission failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission failed",
        description: `Error: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      
      fileArray.forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          invalidFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });
      
      // Show error if any files are invalid
      if (invalidFiles.length > 0) {
        toast({
          title: "File size limit exceeded",
          description: `The following files exceed 10MB limit: ${invalidFiles.join(', ')}`,
          variant: "destructive",
          duration: 5000,
        });
      }
      
      // Only add valid files to the list
      if (validFiles.length > 0) {
        setSubmissionFiles(prev => [...prev, ...validFiles]);
      }
      
      // Always clear the input after processing
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setSubmissionFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  // Get packages that haven't been submitted yet
  const availablePackages = packages.filter(pkg => 
    !submittedReturns.some(ret => ret.packageId === pkg.id)
  );

  // Get unique companies for filter dropdown
  const uniqueCompanies = useMemo(() => {
    return [...new Set(submittedReturns.map(ret => ret.companyName).filter(Boolean))].sort();
  }, [submittedReturns]);

  // Status badge color function
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
      case 'tender_returned':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'awarded':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'under_review':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Filter returns based on search and filter criteria
  const filteredReturns = useMemo(() => {
    return submittedReturns.filter(returnItem => {
      // Search filter
      const matchesSearch = searchQuery === "" || 
        (returnItem.packageNumber || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (returnItem.packageName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (returnItem.companyName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (returnItem.returnedBy || '').toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === "all" || returnItem.status === statusFilter;

      // Company filter
      const matchesCompany = companyFilter === "all" || (returnItem.companyName || '') === companyFilter;

      return matchesSearch && matchesStatus && matchesCompany;
    });
  }, [submittedReturns, searchQuery, statusFilter, companyFilter]);

  if (returnsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page Header removed completely */}

      {/* Main Content */}
      <main className="flex-1 p-6">
        {/* Summary Cards - moved below page header with elegant spacing */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 mt-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Returns</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredReturns.length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Submitted Today</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredReturns.filter(ret => {
                      const today = new Date().toDateString();
                      return new Date(ret.submittedAt).toDateString() === today;
                    }).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Files</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredReturns.reduce((sum, ret) => sum + (ret.filesCount || 0), 0)}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">This Week</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredReturns.filter(ret => {
                      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                      return new Date(ret.submittedAt) >= weekAgo;
                    }).length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Returns Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Tender Returns ({filteredReturns.length})
              </CardTitle>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="text-gray-600 hover:text-gray-900 h-8"
                >
                  {filtersExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3 mr-1" />
                      Hide Filters
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3 mr-1" />
                      Show Filters
                    </>
                  )}
                </Button>
                {availablePackages.length > 0 && (
                  <Button onClick={() => setShowSubmissionDialog(true)}>
                    <FileUp className="h-4 w-4 mr-2" />
                    Submit Return
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          {filtersExpanded && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Search</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                    <Input
                      placeholder="Search by package, company, or person..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="awarded">Awarded</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Company</label>
                  <Select value={companyFilter} onValueChange={setCompanyFilter}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {uniqueCompanies.map(company => (
                        <SelectItem key={company} value={company}>{company}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          )}
          <CardContent className={filtersExpanded ? "pt-0" : ""}>
            {filteredReturns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>{submittedReturns.length === 0 ? "No tender returns submitted yet." : "No tender returns match the current filters."}</p>
                {submittedReturns.length > 0 && filteredReturns.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setCompanyFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <div className="min-w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">Package</TableHead>
                        <TableHead className="min-w-[150px]">Company</TableHead>
                        <TableHead className="min-w-[180px]">Submitted By</TableHead>
                        <TableHead className="min-w-[100px]">Hub Name</TableHead>
                        <TableHead className="min-w-[200px]">Project Name</TableHead>
                        <TableHead className="min-w-[140px]">Submitted Date</TableHead>
                        <TableHead className="min-w-[100px]">Status</TableHead>
                        <TableHead className="min-w-[120px]">Return Reason</TableHead>
                        <TableHead className="min-w-[80px] text-center">Return Files</TableHead>
                        <TableHead className="min-w-[120px] text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReturns.map((returnItem, index) => (
                        <TableRow 
                          key={returnItem.id || `${returnItem.submissionId}-${index}`} 
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedReturn(returnItem);
                            setShowReturnDetailDialog(true);
                          }}
                        >
                          <TableCell className="font-medium">
                            <div>
                              <div className="font-mono text-sm text-blue-600 font-bold">
                                {returnItem.packageNumber || 'N/A'}
                              </div>
                              <div className="font-medium text-gray-900 text-sm">
                                {returnItem.packageName || 'N/A'}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span className="font-medium text-gray-900 truncate">
                                {returnItem.companyName || 'N/A'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm truncate">
                                  {returnItem.returnedBy || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {returnItem.returnedByEmail || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-700 truncate block">
                              {returnItem.hubName || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-700 truncate block">
                              {returnItem.projectName || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">
                                {formatDate(returnItem.submittedAt)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getStatusColor(returnItem.status)} font-medium`}>
                              {returnItem.status === 'submitted' ? 'Submitted' : 
                               returnItem.status === 'tender_returned' ? 'Not Reviewed' :
                               returnItem.status === 'awarded' ? 'Awarded' :
                               returnItem.status === 'rejected' ? 'Not Awarded' :
                               returnItem.status || 'Pending'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-700 truncate block">
                              {returnItem.notes || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <FileText className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium">
                                {returnItem.filesCount || 0}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReturn(returnItem);
                                setShowReturnDetailDialog(true);
                              }}
                              className="h-8 px-3 bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-700 text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Submission Dialog */}
      <Dialog open={showSubmissionDialog} onOpenChange={setShowSubmissionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>Submit Tender Return</DialogTitle>
            <DialogDescription>
              Select a package and upload your tender response files.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-6">
            {/* Package Selection */}
            <div>
              <Label htmlFor="package-select">Select Package</Label>
              <select
                id="package-select"
                className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                value={selectedPackage?.id || ''}
                onChange={(e) => {
                  const pkg = availablePackages.find(p => p.id === e.target.value);
                  setSelectedPackage(pkg || null);
                }}
              >
                <option value="">Choose a package...</option>
                {availablePackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.packageNumber} - {pkg.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div>
              <Label htmlFor="files">Upload Files</Label>
              <div className="mt-1">
                <input
                  id="files"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Accepted formats: PDF, Word, Excel, Images • Maximum 10MB per file
              </p>
            </div>

            {/* Selected Files */}
            {submissionFiles.length > 0 && (
              <div>
                <Label>Selected Files</Label>
                <div className="mt-2 space-y-2">
                  {submissionFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="pb-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes about your submission..."
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 mt-0 !relative">
            <Button variant="outline" onClick={() => setShowSubmissionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTenderReturn}
              disabled={!selectedPackage || submissionFiles.length === 0}
            >
              <Upload className="h-4 w-4 mr-2" />
              Submit Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Detail Dialog */}
      <Dialog open={showReturnDetailDialog} onOpenChange={setShowReturnDetailDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col bg-white border-gray-200 shadow-xl text-gray-900 p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-200">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Tender Return Details - {selectedReturn?.packageName}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Complete details of your tender return submission for {selectedReturn?.packageNumber}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Return Information */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Submission Information</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-900">Package Number:</span>
                    <span className="ml-2 text-gray-800 font-mono">{selectedReturn?.packageNumber}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Package Name:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.packageName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Company:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.recipientCompany || authData?.companyName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Submitted By:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.returnedBy || authData?.email?.split('@')[0] || 'You'}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-900">Email:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.returnedByEmail || authData?.email}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Submitted Date:</span>
                    <span className="ml-2 text-gray-800">
                      {selectedReturn?.submittedAt ? formatDate(selectedReturn.submittedAt) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Project:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.projectName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Hub:</span>
                    <span className="ml-2 text-gray-800">{selectedReturn?.hubName || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {selectedReturn?.packageDescription && (
                <div className="mt-4">
                  <span className="font-medium text-gray-900">Package Description:</span>
                  <p className="mt-1 text-gray-800 bg-white p-3 rounded border border-gray-300">
                    {selectedReturn.packageDescription}
                  </p>
                </div>
              )}
              
              {selectedReturn?.notes && (
                <div className="mt-4">
                  <span className="font-medium text-gray-900">Submission Notes:</span>
                  <p className="mt-1 text-gray-800 bg-white p-3 rounded border border-gray-300">
                    {selectedReturn.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Status and Files */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">Current Status</h4>
                <Badge className={`${getStatusColor(selectedReturn?.status || '')} font-semibold text-lg px-4 py-2`}>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {selectedReturn?.status === 'tender_returned' ? 'Successfully Submitted' : 
                   selectedReturn?.status === 'awarded' ? 'Awarded' :
                   selectedReturn?.status === 'rejected' ? 'Rejected' : selectedReturn?.status}
                </Badge>
                
                {selectedReturn?.isApproved !== undefined && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-900">Approval Status:</span>
                    <div className="mt-1">
                      {selectedReturn.isApproved ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approved
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                    {selectedReturn.approvedBy && (
                      <div className="mt-1 text-sm text-gray-600">
                        Approved by: {selectedReturn.approvedBy}
                      </div>
                    )}
                    {selectedReturn.approvedAt && (
                      <div className="mt-1 text-sm text-gray-600">
                        Approved at: {formatDate(selectedReturn.approvedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">Files Uploaded</h4>
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <span className="text-2xl font-bold text-gray-900">
                    {selectedReturn?.filesCount || 0}
                  </span>
                  <span className="text-gray-700 font-medium">files</span>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">Actions</h4>
                <div className="space-y-2">
                  <Button
                    onClick={async () => {
                      if (!selectedReturn?.submissionId) return;
                      
                      // Check if there are files to download
                      const filesCount = selectedReturn.filesCount || selectedReturn.filesUploaded || 0;
                      if (filesCount === 0) {
                        toast({
                          title: "No Files Available",
                          description: "This tender return has no files to download.",
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      try {
                        // Download all files from this tender return submission
                        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001'}/api/tender-return-files/download-all/${selectedReturn.submissionId}?email=${encodeURIComponent(authData?.email || '')}`, {
                          method: 'GET',
                        });
                        
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.style.display = 'none';
                          a.href = url;
                          a.download = `${selectedReturn.packageNumber}_Tender_Return_Files.zip`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          
                          toast({
                            title: "Download Started",
                            description: "Your files are being downloaded.",
                          });
                        } else if (response.status === 404) {
                          toast({
                            title: "Files Not Found",
                            description: "No files are available for this tender return.",
                            variant: "destructive",
                          });
                        } else {
                          console.error('Failed to download files:', response.status);
                          toast({
                            title: "Download Failed",
                            description: `Failed to download files: ${response.statusText}`,
                            variant: "destructive",
                          });
                        }
                      } catch (error) {
                        console.error('Error downloading files:', error);
                        toast({
                          title: "Download Error",
                          description: "An error occurred while downloading files.",
                          variant: "destructive",
                        });
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    disabled={!selectedReturn?.filesCount || selectedReturn.filesCount === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Files
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}